import type { TrackSourceKind } from '@/lib/db/types';

import { ADSB_AGGREGATORS, AdsbSource } from './adsb';
import { FR24_PLANS, Fr24ApiSource, isFr24Plan } from './fr24-api';
import { sourceFetch } from '@/lib/platform/fetch';

import { createRelayFetch, resolveRelay } from './relay';
import type { SourceCapabilities, TrackSource } from './types';

/**
 * Every network source this build can talk to, as data.
 *
 * The adapters themselves are classes because each holds a little state — a
 * cached OAuth token, a downloaded day file — but *which* sources exist, what
 * each needs from the pilot and what its terms say is a table. A settings page
 * that renders from this table and a search screen that picks from it means
 * adding a fourth source is one row plus its adapter, not a fourth branch in
 * two components.
 *
 * `lookupBy` is the field that earns its place here. FR24 finds a flight by
 * registration; the ADS-B globes index by ICAO 24-bit address and
 * have no registration lookup at all. That is not a detail to discover through
 * an empty result list — the search form reads this and asks for the right
 * thing.
 */

export type ApiSourceId = 'fr24-api' | 'adsb-lol' | 'airplanes-live';

export type CredentialFieldKind = 'secret' | 'text' | 'toggle' | 'select';

export interface CredentialField {
  /** Stable id; becomes part of the settings key this value is stored under. */
  key: string;
  label: string;
  kind: CredentialFieldKind;
  /** Whether the source is unusable without it. */
  required: boolean;
  placeholder?: string;
  help: string;
  /** The choices for a `'select'` field. An empty value means "not set". */
  options?: readonly { value: string; label: string }[];
}

export interface ApiSourceDescriptor {
  id: ApiSourceId;
  label: string;
  /** What a track fetched from here is stored as. */
  kind: TrackSourceKind;
  /** How a flight is looked up. `'icao-hex'` sources cannot search by registration. */
  lookupBy: 'registration' | 'icao-hex';
  capabilities: SourceCapabilities;
  credentials: readonly CredentialField[];
  /** Licence and permitted use, in one line. Shown wherever the source is offered. */
  terms: string;
  docsUrl: string;
  /** One paragraph a pilot reads before deciding to configure it. */
  summary: string;
}

const FR24: ApiSourceDescriptor = {
  id: 'fr24-api',
  label: 'Flightradar24 API',
  kind: 'fr24-api',
  lookupBy: 'registration',
  capabilities: { search: true, historyDays: null, needsKey: true, metered: true },
  credentials: [
    {
      key: 'token',
      label: 'API token',
      kind: 'secret',
      required: true,
      placeholder: 'Bearer token from fr24api.flightradar24.com',
      help: 'Kept in this browser only. Sent straight to Flightradar24, never through a server.',
    },
    {
      /*
       * The one thing that decides how far back a search can reach, and the
       * API has no endpoint that reports it. Without it the search screen can
       * only state the data floor and list the plan limits; with it, a date
       * the plan cannot reach is refused before a credit is spent.
       */
      key: 'plan',
      label: 'Plan',
      kind: 'select',
      required: false,
      help: 'Sets how far back a search can reach.',
      options: [
        { value: '', label: 'Not set' },
        ...Object.entries(FR24_PLANS).map(([value, plan]) => ({
          value,
          label:
            plan.historyDays === null
              ? `${plan.label}: full history`
              : `${plan.label}: ${plan.historyDays >= 365 ? `${String(plan.historyDays / 365)} years` : `${String(plan.historyDays)} days`}`,
        })),
      ],
    },
    {
      key: 'sandbox',
      label: 'Use the sandbox',
      kind: 'toggle',
      required: false,
      help: 'Returns fixed sample data and spends no credits. The right way to check a key works before searching for real.',
    },
  ],
  terms:
    'Commercial, credit-priced. Two calls per flight: one to find its id, one for its positions.',
  docsUrl: 'https://fr24api.flightradar24.com/docs',
  summary:
    'The best coverage of the three for New Zealand GA, and the only paid one. Searches by registration over any date range the plan reaches.',
};

const ADSB: readonly ApiSourceDescriptor[] = ADSB_AGGREGATORS.map((aggregator) => ({
  id: aggregator.id as ApiSourceId,
  label: aggregator.label,
  kind: 'adsb' as const,
  lookupBy: 'icao-hex' as const,
  capabilities: { search: true, historyDays: null, needsKey: false, metered: false },
  credentials: [],
  terms: aggregator.terms,
  docsUrl: aggregator.docsUrl,
  summary:
    'Free and unmetered, with one file per aircraft per day — so a search downloads the tracks as it goes and picking one afterwards costs nothing. A day file can hold several flights; they are split on coverage gaps and time on the ground.',
}));

export const API_SOURCES: readonly ApiSourceDescriptor[] = [FR24, ...ADSB];

export function apiSourceById(id: string): ApiSourceDescriptor | undefined {
  return API_SOURCES.find((source) => source.id === id);
}

export function isApiSourceId(value: string): value is ApiSourceId {
  return API_SOURCES.some((source) => source.id === value);
}

export interface CreateSourceOptions {
  fetchImpl?: typeof globalThis.fetch;
  /**
   * The relay the pilot has named, if any (`relay.ts`). Omitted means "whatever
   * this build's environment implies", which is the dev server's proxy in
   * development and a direct request otherwise.
   */
  relay?: string | undefined;
  importedAt?: string;
  newId?: () => string;
  now?: () => number;
}

/**
 * Build the adapter for a source from the values the pilot has stored.
 *
 * @param credentials  keyed by `CredentialField.key`. Missing keys are the
 *   normal case for an unconfigured source, and are handled by the adapter —
 *   FR24 throws a `no-key` error naming Settings.
 * @returns `undefined` for an id this build does not know.
 */
export function createTrackSource(
  id: string,
  credentials: Readonly<Record<string, string>> = {},
  options: CreateSourceOptions = {},
): TrackSource | undefined {
  /*
   * None of these three sources sends a CORS header, so a request straight from
   * the page is refused before the adapter ever sees a response — see
   * `relay.ts`. Every adapter gets the relay-aware `fetch` from here rather than
   * each call site remembering to ask for it.
   *
   * An injected `fetchImpl` is left alone: it is a test's own transport, and the
   * URL it asserts on is the real one the adapter built.
   */
  const fetchImpl =
    options.fetchImpl ??
    createRelayFetch(
      resolveRelay({
        dev: import.meta.env.DEV,
        buildTime: import.meta.env.VITE_SOURCE_RELAY as string | undefined,
        ...(options.relay === undefined ? {} : { setting: options.relay }),
      }),
      // In the desktop app, a native request with no origin to refuse.
      sourceFetch,
    );

  const shared = {
    fetchImpl,
    ...(options.importedAt === undefined ? {} : { importedAt: options.importedAt }),
    ...(options.newId === undefined ? {} : { newId: options.newId }),
    ...(options.now === undefined ? {} : { now: options.now }),
  };

  if (id === 'fr24-api') {
    return new Fr24ApiSource({
      apiKey: credentials.token ?? '',
      // A toggle is stored as the string a checkbox produces, not a boolean:
      // every settings row in this app is one JSON value and keeping them all
      // strings means one parser, not one per field type.
      sandbox: credentials.sandbox === 'true',
      ...(isFr24Plan(credentials.plan) ? { plan: credentials.plan } : {}),
      ...shared,
    });
  }

  const aggregator = ADSB_AGGREGATORS.find((entry) => entry.id === id);
  if (aggregator !== undefined) {
    return new AdsbSource({ aggregator, ...shared });
  }

  return undefined;
}
